import Checkbox from '@material-ui/core/Checkbox';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment-timezone';
import React from 'react';
import EnhancedTableHead, { getSorting, HeadRow, Order, sortRows } from '../common/EnhancedTableHead';
import LinkRouter from '../common/LinkRouter';
import TimestampTooltip from '../common/TimestampTooltip';
import TablePagination from '@material-ui/core/TablePagination';
import { useObserver } from 'mobx-react-lite';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    descriptionCol: {
      maxWidth: 150,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis'
    }
  })
);

interface Props {
  families: any[];
  page: number;
  onPageChange: (newPage: number) => void;
  onToggleSubscription: (family: any) => void;
}

const headRows: HeadRow[] = [
  { id: 'name', disablePadding: true, label: 'Name' },
  { id: 'description', disablePadding: false, label: 'Description' },
  { id: 'workflowCount', disablePadding: false, label: 'Workflows' },
  { id: 'owner', disablePadding: false, label: 'Owner' },
  { id: 'lastModified', disablePadding: false, label: 'Last Modified' },
  { id: 'subscribed', disablePadding: false, label: 'Subscribed' }
];

function FamiliesTable(props: Props) {
  const { families, page, onPageChange } = props;
  const classes = useStyles({});
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<string>('lastModified');
  const [rowsPerPage, setRowsPerPage] = React.useState(20);

  function handleRequestSort(event: React.MouseEvent<unknown>, property: string) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    onPageChange(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleCheckboxChange = (family: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
    props.onToggleSubscription(family);
  };

  const sortedRows = sortRows(families, getSorting(order, orderBy));

  return useObserver(() => (
    <div>
      <Table>
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          fields={headRows}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
            <TableRow key={row.id}>
              <TableCell component="th" scope="row">
                <LinkRouter to={`/families/${row.id}`}>{row.name}</LinkRouter>
              </TableCell>
              <TableCell>
                <Tooltip title={row.description || ''}>
                  <div className={classes.descriptionCol}>{row.description}</div>
                </Tooltip>
              </TableCell>
              <TableCell>{row.workflowCount}</TableCell>
              <TableCell>{row.owner}</TableCell>
              <TableCell>
                <TimestampTooltip timestamp={row.lastModified}>
                  <div>{moment(row.lastModified).fromNow()}</div>
                </TimestampTooltip>
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={row.subscribed}
                  onChange={handleCheckboxChange(row)}
                  value="true"
                  color="primary"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={props.families.length}
        rowsPerPage={rowsPerPage}
        page={page}
        backIconButtonProps={{
          'aria-label': 'previous page',
        }}
        nextIconButtonProps={{
          'aria-label': 'next page',
        }}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  ));
};

export default FamiliesTable;
